import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-rest",
  title: "Bible verses about rest: 10 passages and what they ask",
  h1: "Bible verses about rest",
  description:
    "Ten passages on rest and sabbath from the World English Bible, with notes — including why rest is commanded rather than earned, and who the fourth commandment protects.",
  targets: [
    "bible verses about rest",
    "scripture about sabbath rest",
    "what does the bible say about rest",
  ],
  published: "2026-08-26",
  standfirst:
    "Rest in scripture is not what is left over when the work is finished. It is commanded, scheduled, and aimed first at the people with the least power to take it.",
  takeaways: [
    "Sabbath is the only one of the ten commandments that has to be argued for today, and the version in Deuteronomy 5 grounds it in having been slaves — rest is framed as the opposite of bondage.",
    "Mark 2:27 states the direction of the whole thing: the Sabbath was made for people, not people for the Sabbath.",
    "The rest offered in Matthew 11:28 comes with a yoke, which means it is not the absence of work but a different way of carrying it.",
    "The commandment covers the household, the workers and the animals — it is a protection for whoever cannot decide their own hours, not a privilege for whoever can.",
  ],
  intro: [
    {
      type: "p",
      text: "Almost everybody who looks up verses about rest is tired, and almost everybody who is tired has already decided that rest is what happens once the necessary things are done. Scripture puts it the other way round. Rest is scheduled before the work is finished, on purpose, at a fixed interval, whether or not the harvest is in — which is either an absurd instruction or the entire point of it.",
    },
    {
      type: "p",
      text: "Two things in the passages below are easy to miss. The first is who the command protects: the fourth commandment covers the household, the employees, the foreign worker and the animals, which makes it labour legislation as much as liturgy. The second is that the rest offered in the gospels is not emptiness — it arrives attached to a yoke. Both are noted where they appear.",
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain. Trimmed passages are marked with an ellipsis.",
    },
  ],
  verses: [
    {
      verse: {
        text: "On the seventh day God finished his work which he had done; and he rested on the seventh day from all his work which he had done.",
        ref: "Genesis 2:2",
      },
      note: "The pattern is established before there is any tiredness in the story, which rules out exhaustion as its reason. Rest is presented as a completion rather than a recovery — the last thing made is a day with nothing to make in it.",
    },
    {
      verse: {
        text: "Remember the Sabbath day, to keep it holy … You shall not do any work in it, you, nor your son, nor your daughter, your male servant, nor your female servant, nor your livestock.",
        ref: "Exodus 20:8, 10",
      },
      note: "The list is the argument. Everyone who could be made to work by somebody else is named, including the animals. Whoever has the power to keep a household working is the person the command is aimed at, which makes it a limit on employers long before it is a comfort to the tired.",
    },
    {
      verse: {
        text: "You shall remember that you were a servant in the land of Egypt … therefore Yahweh your God commanded you to keep the Sabbath day.",
        ref: "Deuteronomy 5:15",
      },
      note: "The second telling of the commandment gives a completely different reason from the first: not creation, but slavery. A people who had been worked without limit are told to build the limit into the week. Rest here is the practical proof of not being owned.",
    },
    {
      verse: {
        text: "The Sabbath was made for man, not man for the Sabbath.",
        ref: "Mark 2:27",
      },
      note: "Said in an argument about whether hungry people could pick grain on the day off. The sentence settles the direction of the whole institution — it exists to serve people, and any version of it that grinds them is being read upside down. It is also the verse most often used to skip it entirely, which is a third misreading.",
    },
    {
      verse: {
        text: "Come to me, all you who labor and are heavily burdened, and I will give you rest.",
        ref: "Matthew 11:28",
      },
      note: "Addressed to people carrying something, not to people with time on their hands. Read with the two verses after it — take my yoke, learn from me — the offer is plainly not an empty afternoon but a lighter way of being harnessed.",
    },
    {
      verse: {
        text: "Come away into a deserted place, and rest awhile. For there were many coming and going, and they had no leisure so much as to eat.",
        ref: "Mark 6:31",
      },
      note: "The most modern-sounding verse in the Bible. The description is of a crowd with constant demand and no gaps, and the instruction is not to work harder at being present but to physically leave. Worth noting what happens next in the chapter: the crowd follows, and the rest is interrupted. Even this one does not go to plan.",
    },
    {
      verse: {
        text: "He makes me lie down in green pastures. He leads me beside still waters.",
        ref: "Psalm 23:2",
      },
      note: "Makes is the word to sit with. Sheep do not lie down while anxious, and the picture is of conditions being arranged until rest becomes possible — which is closer to how rest actually works than any amount of deciding to relax.",
    },
    {
      verse: {
        text: "My presence will go with you, and I will give you rest.",
        ref: "Exodus 33:14",
      },
      note: "Spoken to a leader in the middle of an argument about whether he can keep going. Rest is defined here as accompanied rather than as stopped — the promise is not that the journey ends but that he will not be doing it alone.",
    },
    {
      verse: {
        text: "There remains therefore a Sabbath rest for the people of God.",
        ref: "Hebrews 4:9",
      },
      note: "The letter's argument is that the weekly day pointed at something larger and unfinished. Useful in both directions: it stops the Sabbath being a rule to be scored on, and it stops rest being reduced to a self-care technique, since what is being described is bigger than a good weekend.",
    },
    {
      verse: {
        text: "Return to your rest, my soul, for Yahweh has dealt bountifully with you.",
        ref: "Psalm 116:7",
      },
      note: "Return implies a place already known and left. Most people's rest is not missing, it has been vacated — and the psalm treats going back to it as an act of memory: he has dealt bountifully, therefore there is no emergency requiring your vigilance tonight.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "putting-one-in-the-week",
      text: "Putting one of these into the week",
    },
    {
      type: "p",
      text: "The honest difficulty is not theological. Very few people are held back from rest by a conviction that the Sabbath is abolished; they are held back by a phone, which makes every hour a possible working hour and every gap a possible feed. A day off that is spent answering messages is not a day off, and it is worth being blunt that this is now the ordinary way the commandment gets broken.",
    },
    {
      type: "p",
      text: "Start with hours rather than a day. Choose a fixed block — Saturday evening, Sunday afternoon — and make it unreachable rather than merely unscheduled, because an unscheduled hour will fill itself. {The-digital-sabbath-piece} sets out a version that people actually keep, along with the two mistakes that end most attempts in the first fortnight.",
      links: { "The-digital-sabbath-piece": "/blog/digital-sabbath" },
    },
    {
      type: "h2",
      id: "a-prayer-for-rest",
      text: "A prayer for a day off",
    },
    {
      type: "p",
      text: "Lord, you rested when nothing required it, and you commanded rest for people who had never been allowed any. Return me to mine. Let the work that is unfinished stay unfinished until tomorrow, and let me be a person who does not make anybody else work today. Amen.",
    },
    {
      type: "note",
      text: "The fourth commandment names the people who cannot choose their own hours. Whatever else keeping it means, it means checking who is working because you are.",
    },
  ],
  faq: [
    {
      q: "Are Christians required to keep the Sabbath?",
      a: "Christian traditions differ, and this page does not settle it — Romans 14:5 and Colossians 2:16 are read one way by some and another by others. What is not in dispute is the pattern: a fixed, recurring stop that is not earned by finishing the work. The rhythm is the part worth arguing over.",
    },
    {
      q: "What is the difference between rest and laziness in the Bible?",
      a: "Proverbs is severe about sloth and scripture is emphatic about Sabbath, which only looks contradictory from a distance. Sabbath rest is bounded, deliberate and confident that the world will hold without you for a day. Sloth is unbounded and usually avoidant. The tell is whether the stopping was chosen in advance.",
    },
    {
      q: "Which translation is used here?",
      a: "The World English Bible, which is in the public domain, so the passages can be printed in full rather than paraphrased.",
    },
  ],
  related: [
    "digital-sabbath",
    "social-media-fast-guide",
    "morning-quiet-time-routine",
  ],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-time",
  ],
};
